import { useTranslation } from "react-i18next";

import type {
  PlanningConfig,
  useUpdatePlanningConfig,
} from "@/api/planningConfig";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

interface ForecastMethodSectionProps {
  config: PlanningConfig;
  updateConfig: ReturnType<typeof useUpdatePlanningConfig>;
  disabled?: boolean;
}

const METHODS = ["ets", "croston", "mean", "static"] as const;
type Method = (typeof METHODS)[number];
const LABELS: Record<Method, string> = {
  ets: "ETS",
  croston: "Croston",
  mean: "Mean",
  static: "Static",
};

export function ForecastMethodSection({
  config,
  updateConfig,
  disabled = false,
}: ForecastMethodSectionProps) {
  const { t } = useTranslation();
  const value = (config.method ?? "ets") as Method;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
          {t("items.detail.method.title")}
        </span>
      </div>
      <ToggleGroup
        type="single"
        value={value}
        onValueChange={(v) => {
          if (!v) return;
          if (v === "static") {
            // Static needs an authored rate; seed 0 (the valid reactive default)
            // so the config is valid immediately. The input lets the user raise it.
            updateConfig.mutate({
              item: config.item,
              location: config.location,
              method: "static",
              assumedMonthlyDemand: 0,
            });
          } else {
            updateConfig.mutate({
              item: config.item,
              location: config.location,
              method: v as Method,
            });
          }
        }}
        disabled={disabled || updateConfig.isPending}
      >
        {METHODS.map((m) => (
          <ToggleGroupItem key={m} value={m} aria-label={LABELS[m]}>
            {LABELS[m]}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}
